import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../store/reducers";
import { PageView, ReportTypes } from "../../../constants/app.constant";
import { reportDataThunk } from "../../../store/slices/reportsSlice";
import Loader from "../../loader";
import PayGapBarChart from "../Charts/PayGapBarChart/PayGapBarChart";
import PayPieChart from "../Charts/PayPieChart/PayPieChart";

import styles from "./HeadlinePayEquity.module.scss";

const HeadlinePayEquity = ({ pageView = PageView.CHART }: any) => {
  const [peReportData, setPeReportData] = useState<any>({});
  const dispatch = useDispatch();

  const { headlinePayEquity } = useSelector((state: RootState) => {
    return state.content.reports;
  });

  const { globalFilters } = useSelector((state: RootState) => {
    return state.reports;
  });

  const { isReportsLoading, reportsData } = useSelector((state: RootState) => {
    return state.reports;
  });

  const initReportData = () => {
    const _globalFilters = { ...globalFilters };

    delete _globalFilters.hayPointsMin;
    delete _globalFilters.hayPointsMax;

    const payload = {
      type: ReportTypes.HEADLINE_PAY_EQUITY_STATISTICS,
      filters: _globalFilters
    };

    dispatch(reportDataThunk(payload));
  };

  useEffect(() => {
    let { headCountAnalysis, averageHeadlinePayGap, medianHeadlinePayGap } =
      reportsData[ReportTypes.HEADLINE_PAY_EQUITY_STATISTICS] || {};

    if (headCountAnalysis) {
      headCountAnalysis = {
        female: +(+headCountAnalysis.female).toFixed(2),
        male: +(+headCountAnalysis.male).toFixed(2)
      };
      const total = headCountAnalysis.female + headCountAnalysis.male;
      headCountAnalysis = {
        ...headCountAnalysis,
        femaleAvg: +((headCountAnalysis.female / total) * 100).toFixed(2),
        maleAvg: +((headCountAnalysis.male / total) * 100).toFixed(2)
      };
    }

    if (averageHeadlinePayGap) {
      averageHeadlinePayGap = {
        female: +(+averageHeadlinePayGap.female).toFixed(2),
        male: +(+averageHeadlinePayGap.male).toFixed(2)
      };
      const total = averageHeadlinePayGap.female + averageHeadlinePayGap.male;
      averageHeadlinePayGap = {
        ...averageHeadlinePayGap,
        femaleAvg: +((averageHeadlinePayGap.female / total) * 100).toFixed(2),
        maleAvg: +((averageHeadlinePayGap.male / total) * 100).toFixed(2)
      };
    }

    if (medianHeadlinePayGap) {
      medianHeadlinePayGap = {
        female: +(+medianHeadlinePayGap.female).toFixed(2),
        male: +(+medianHeadlinePayGap.male).toFixed(2)
      };
      const total = medianHeadlinePayGap.female + medianHeadlinePayGap.male;
      medianHeadlinePayGap = {
        ...medianHeadlinePayGap,
        femaleAvg: +((medianHeadlinePayGap.female / total) * 100).toFixed(2),
        maleAvg: +((medianHeadlinePayGap.male / total) * 100).toFixed(2)
      };
    }
    const _reportsData = {
      ...reportsData,
      headCountAnalysis,
      averageHeadlinePayGap,
      medianHeadlinePayGap
    };

    setPeReportData(_reportsData || {});
  }, [reportsData]);

  useEffect(() => {
    if (globalFilters) {
      initReportData();
    }
  }, [globalFilters]);

  return (
    <div className={styles.reportContainer}>
      {isReportsLoading && <Loader />}
      <div className={styles.description}>{headlinePayEquity.description}</div>
      {pageView === "LIST" && (
        <div className={styles.listViewContainer}>
          <div className={styles.listContainer}>
            <div className={styles.listCard}>
              <h3 className={styles.cardTitle}>
                {headlinePayEquity.headcountAnalysis}
              </h3>
              <div className={styles.listWrapper}>
                <div className={styles.listItem}>
                  <div className={`${styles.listLabel} ${styles.listTitle}`}>
                    Gender group
                  </div>
                  <div className={`${styles.listValue} ${styles.listTitle}`}>
                    Percentage
                  </div>
                </div>
                <div className={styles.listItem}>
                  <div className={styles.listLabel}>Female</div>
                  <div className={styles.listValue}>
                    {peReportData.headCountAnalysis?.femaleAvg}%
                  </div>
                </div>
                <div className={styles.listItem}>
                  <div className={styles.listLabel}>Male</div>
                  <div className={styles.listValue}>
                    {peReportData.headCountAnalysis?.maleAvg}%
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className={styles.listContainer}>
            <div className={styles.listCard}>
              <h3 className={styles.cardTitle}>Average Headline Pay Gap</h3>
              <div className={styles.listWrapper}>
                <div className={styles.listItem}>
                  <div className={`${styles.listLabel} ${styles.listTitle}`}>
                    Gender group
                  </div>
                  <div className={`${styles.listValue} ${styles.listTitle}`}>
                    Percentage
                  </div>
                </div>
                <div className={styles.listItem}>
                  <div className={styles.listLabel}>Female</div>
                  <div className={styles.listValue}>
                    {peReportData.averageHeadlinePayGap?.femaleAvg}%
                  </div>
                </div>
                <div className={styles.listItem}>
                  <div className={styles.listLabel}>Male</div>
                  <div className={styles.listValue}>
                    {peReportData.averageHeadlinePayGap?.maleAvg}%
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.listCard}>
              <h3 className={styles.cardTitle}>Median Headline Pay Gap</h3>
              <div className={styles.listWrapper}>
                <div className={styles.listItem}>
                  <div className={`${styles.listLabel} ${styles.listTitle}`}>
                    Gender group
                  </div>
                  <div className={`${styles.listValue} ${styles.listTitle}`}>
                    Percentage
                  </div>
                </div>
                <div className={styles.listItem}>
                  <div className={styles.listLabel}>Female</div>
                  <div className={styles.listValue}>
                    {peReportData.medianHeadlinePayGap?.femaleAvg}%
                  </div>
                </div>
                <div className={styles.listItem}>
                  <div className={styles.listLabel}>Male</div>
                  <div className={styles.listValue}>
                    {peReportData.medianHeadlinePayGap?.maleAvg}%
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {pageView === "CHART" && (
        <div className={styles.chartContainer}>
          <div className={styles.chartWrappers}>
            <PayPieChart
              title="Headcount Analysis"
              description="This chart shows the relative proportion of your employees by
                gender."
              isLoading={isReportsLoading}
              data={[
                peReportData.headCountAnalysis?.femaleAvg,
                peReportData.headCountAnalysis?.maleAvg
              ]}
            />

            <PayGapBarChart
              title="Average Headline Pay Gap"
              description="This chart shows the average (mean) pay by gender and the percentage
              difference between them."
              data={[
                peReportData.averageHeadlinePayGap?.femaleAvg,
                peReportData.averageHeadlinePayGap?.maleAvg
              ]}
            />

            <PayGapBarChart
              title="Median Headline Pay Gap"
              description="This chart shows the median pay position by gender and the
              percentile difference between them."
              data={[
                peReportData.medianHeadlinePayGap?.femaleAvg,
                peReportData.medianHeadlinePayGap?.maleAvg
              ]}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default HeadlinePayEquity;
